import { CheckIcon, CloseIcon, TrashIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import formatTimeAgo from '@orn/shared-web/utils/datetime/formatTimeAgo';
import * as React from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { AssistantFeedbackCommentTransition } from '../../__generated__/globalTypes';
import IconButton from '../../components/IconButton';

import { transitionBadgeLabel } from './assistantFeedbackView';

interface IAssistantFeedbackCommentProps {
  authorEmail: string | null;
  authorUserId: string;
  transition: AssistantFeedbackCommentTransition | null;
  body: string;
  createdAt: string;
  /** Whether the viewing admin authored this comment (gates the delete control). */
  canDelete: boolean;
  isDeleting?: boolean;
  onDelete?: () => void;
}

/**
 * One comment in a feedback row's inline triage thread: a metadata header
 * (author + time + optional status-change badge) above a markdown-rendered
 * body. Markdown styling mirrors {@link ReleaseBodyMarkdown} so operator notes
 * read consistently across the admin surface. An author-only delete control
 * (with a two-click inline confirm) appears when `canDelete` is set.
 */
const AssistantFeedbackComment = ({
  authorEmail,
  authorUserId,
  transition,
  body,
  createdAt,
  canDelete,
  isDeleting = false,
  onDelete,
}: IAssistantFeedbackCommentProps) => {
  const badge = transitionBadgeLabel(transition);
  const [confirming, setConfirming] = React.useState(false);
  return (
    <div className="border-border-primary border-l-2 py-1 pl-3">
      <div className="text-fg-secondary mb-1 flex items-center gap-2 text-[11px]">
        <span className="font-mono" title={authorUserId}>
          {authorEmail ?? authorUserId}
        </span>
        <span className="text-fg-tertiary" title={new Date(createdAt).toISOString()}>
          {formatTimeAgo(createdAt)}
        </span>
        {badge && (
          <span
            className={cx(
              'border-border-primary rounded-sm border px-1 py-0.5 text-[10px] font-medium uppercase tracking-wide',
              transition === AssistantFeedbackCommentTransition.Resolved
                ? 'text-fg-success-primary'
                : 'text-fg-warning-primary'
            )}
          >
            {badge}
          </span>
        )}
        {canDelete && (
          <span className="ml-auto flex items-center gap-1.5">
            {confirming ? (
              <>
                <IconButton
                  label="Confirm delete comment"
                  tone="danger"
                  disabled={isDeleting}
                  onClick={() => onDelete?.()}
                >
                  <CheckIcon className="size-3.5" />
                </IconButton>
                <IconButton label="Cancel delete" disabled={isDeleting} onClick={() => setConfirming(false)}>
                  <CloseIcon className="size-3.5" />
                </IconButton>
              </>
            ) : (
              <IconButton label="Delete comment" onClick={() => setConfirming(true)}>
                <TrashIcon className="size-3.5" />
              </IconButton>
            )}
          </span>
        )}
      </div>
      <div className="text-fg-primary [&_a]:text-fg-brand-primary [&_code]:text-fg-warning-primary text-sm leading-relaxed [&_a]:underline [&_a]:underline-offset-2 [&_code]:rounded-sm [&_code]:border [&_code]:border-[#61646c] [&_code]:bg-[#22262f] [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.85em] [&_li]:my-0.5 [&_p]:my-1 [&_ul]:list-disc [&_ul]:pl-5">
        <Markdown
          remarkPlugins={[remarkGfm]}
          components={{
            a: ({ href, children, ...props }) => (
              <a href={href} target="_blank" rel="noreferrer noopener" {...props}>
                {children}
              </a>
            ),
          }}
        >
          {body}
        </Markdown>
      </div>
    </div>
  );
};

export default AssistantFeedbackComment;
